// /free-home-assessment — Dedicated AeroMax Hot Water Heat Pump assessment.
// • Not in the main nav. Reachable via footer + every Book Free Assessment CTA.
// • Auto-fills from sessionStorage prefill (Home Show staff capture) when
//   present, or from ?mode=staff / ?source query params.
// • Source tracking: Website Assessment (default) | Home Show Assessment
//   (when prefill present or mode=staff) — preserves linked Home Show meta.
import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { submitLead, getProspectDetails, getAssessmentMembers, getAssessorSlots, convertProspect } from "@/lib/api";
import {
  getAssessmentPrefill,
  clearAssessmentPrefill,
} from "@/lib/assessmentPrefill";
import AddressAutocomplete from "@/components/forms/AddressAutocomplete";
import Reveal from "@/components/layout/Reveal";
import {
  CheckCircle2,
  Home as HomeIcon,
  Sparkles,
  TrendingUp,
  Wrench,
  Banknote,
  Headphones,
  Camera,
  Info,
  ArrowRight,
} from "lucide-react";

const SYSTEM_TYPES = [
  "Electric cylinder",
  "Gas storage",
  "Gas continuous",
  "LPG",
  "Solar hot water",
  "Unsure",
  "Other",
];
const ENERGY_SOURCES = ["Electricity", "Natural gas", "LPG bottles", "Unsure"];
const USAGE_LEVELS = ["Low", "Medium", "High", "Unsure"];
const SYSTEM_AGES = ["Less than 5 years", "5–10 years", "10+ years", "Unsure"];
const FINANCE_INTEREST = ["Yes", "No", "Maybe"];

// CRM enum keys → assessment form option labels.
const CRM_SYSTEM_TO_FORM = {
  "ELECTRIC-CYLINDER": "Electric cylinder",
  "GAS-STORAGE": "Gas storage",
  "GAS-CONTINUOUS": "Gas continuous",
  "LPG": "LPG",
  "SOLAR-HOT-WATER": "Solar hot water",
  "UNSURE": "Unsure",
  "OTHER": "Other",
};
const CRM_USAGE_TO_FORM = { LOW: "Low", MEDIUM: "Medium", HIGH: "High", UNSURE: "Unsure" };

// Form option labels → CRM enum keys (for the prospect/convert payload).
const invertMap = (obj) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));
const FORM_TO_CRM_SYSTEM = invertMap(CRM_SYSTEM_TO_FORM);
const FORM_TO_CRM_USAGE = invertMap(CRM_USAGE_TO_FORM);
const FORM_TO_CRM_ENERGY = {
  "Electricity": "ELECTRICITY",
  "Natural gas": "NATURAL-GAS",
  "LPG bottles": "LPG-BOTTLES",
  "Unsure": "UNSURE",
};
const FORM_TO_CRM_AGE = {
  "Less than 5 years": "LESS-THAN-5-YEARS",
  "5–10 years": "5-10-YEARS",
  "10+ years": "10-PLUS-YEARS",
  "Unsure": "UNSURE",
};

// CRM prospect-details → assessment form fields (only the non-empty ones).
function mapProspectToForm(d) {
  const out = {};
  if (d.name) out.name = d.name;
  if (d.email) out.email = d.email;
  if (d.phone) out.phone = d.phone;
  if (d.address) out.address = d.address;
  if (d.suburb) out.suburb = d.suburb;
  if (d.city) out.city = d.city;
  if (d.postcode) out.postal_code = d.postcode;
  if (d.numberOfPeopleInHousehold) out.household_size = String(d.numberOfPeopleInHousehold);
  const sys = CRM_SYSTEM_TO_FORM[d.currentHotWaterSystem?.key];
  if (sys) out.current_system = sys;
  const usage = CRM_USAGE_TO_FORM[d.approximateHotWaterUsage?.key];
  if (usage) out.usage_level = usage;
  const energy = d.currentEnergySource?.label;
  if (energy && ENERGY_SOURCES.includes(energy)) out.energy_source = energy;
  const age = d.currentSystemAge?.label;
  if (age && SYSTEM_AGES.includes(age)) out.system_age = age;
  if (d.message) out.notes = d.message;
  return out;
}

// ─── Slot helpers ─────────────────────────────────────────────────────────────
// The CRM returns discrete bookable slots per assessor/date, each like
// { scheduleDate, scheduleStartTime: "09:00 AM", scheduleEndTime: "10:00 AM" }.
// We render them in a dropdown; the chosen slot's start/end are already in the
// CRM's "hh:mm AM" format and go straight back in the convert payload.

// "09:00 AM" → minutes since midnight (null if unparseable) — used to sort slots.
function parseClockTime(str) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(str || "").trim());
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + Number(m[2]);
}

// Stable <option> value / form key for a slot: "09:00 AM|10:00 AM".
const slotKey = (s) => `${s.scheduleStartTime}|${s.scheduleEndTime}`;

// Pristine form state — reused for the initial mount and the Clear/reset action.
const makeInitialForm = (todayISO) => ({
  // Customer details
  name: "",
  email: "",
  phone: "",
  // `street` is the address line without suburb/city/postcode (CRM address).
  // suburb/city/postcode auto-fill from the autocomplete and stay editable.
  address: "",
  street: "",
  suburb: "",
  city: "",
  postal_code: "",
  country: "",
  lat: null,
  lng: null,
  // Home & usage
  household_size: "",
  current_system: "",
  energy_source: "",
  usage_level: "",
  system_age: "",
  // Assessment details
  interested_finance: "",
  notes: "",
  consent: false,
  // CRM prospect id carried over from the savings calculator (if any)
  crm_prospect_id: "",
  // Scheduling (CRM assessment booking) — assessor + chosen slot
  assessor_id: "",
  schedule_date: todayISO, // default to today; user can change it
  slot_start: "",
  slot_end: "",
});

const BENEFITS = [
  {
    icon: TrendingUp,
    title: "Confirm your real-world savings",
    body:
      "An on-site review of your usage and tariffs verifies the savings estimate against your real home.",
  },
  {
    icon: Wrench,
    title: "Check installation suitability",
    body:
      "We check location, plumbing, electrical, drainage and access so the system is right for your home.",
  },
  {
    icon: Banknote,
    title: "Review green loan options",
    body:
      "We help prepare the quote and product info your bank may need for an eligible green home-loan top-up.",
  },
  {
    icon: Headphones,
    title: "Get expert AeroMax guidance",
    body:
      "Talk directly to an AeroMax specialist — no pressure, no obligation.",
  },
];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-16 sm:pt-20 sm:pb-20">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <HomeIcon className="h-3 w-3 text-[#10B981]" /> Free Assessment
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Book your free AeroMax <span className="text-[#10B981]">hot water heat pump</span> assessment.
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl">
            Find out whether a high-efficiency hot water heat pump is right for your home. Our team will review your current system, household usage, installation suitability, estimated savings, and available finance options.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function BenefitGrid() {
  return (
    <section className="py-12 sm:py-16 bg-white border-b border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {BENEFITS.map((b, i) => (
          <Reveal key={i} delay={i * 60}>
            <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6 hover:shadow-lg hover:shadow-emerald-500/8 hover:border-[#10B981]/30 transition-all">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981]">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="font-heading mt-3 text-base sm:text-lg font-bold text-[#0B132B] leading-snug">
                {b.title}
              </h3>
              <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">
                {b.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FormField({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#0B132B]">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-[#64748B]">{hint}</p>}
    </div>
  );
}

function SelectField({ value, onChange, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
    >
      <option value="">Select an option…</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function AssessmentForm({ initialPrefill, prefillSource }) {
  // Local "today" (YYYY-MM-DD) — used as the default + minimum assessment date.
  const now = new Date();
  const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const [form, setForm] = useState(() => makeInitialForm(todayISO));
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [prefillBannerVisible, setPrefillBannerVisible] = useState(false);
  // CRM-sourced scheduling options
  const [assessors, setAssessors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]); // photos for the CRM convert

  // Apply prefill once on mount. Customers/staff can still freely edit.
  useEffect(() => {
    if (!initialPrefill) return;
    setForm((s) => ({
      ...s,
      ...Object.fromEntries(
        Object.entries(initialPrefill).filter(
          ([, v]) => v !== undefined && v !== null && v !== ""
        )
      ),
    }));
    // Show the banner only when real customer details were carried over.
    if (initialPrefill.name || initialPrefill.email) setPrefillBannerVisible(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // With a CRM prospect id (URL ?prospectId=), fetch its details and fill only
  // still-empty fields — never overwriting a prefill or a user edit.
  useEffect(() => {
    const pid = initialPrefill?.crm_prospect_id;
    if (!pid) return;
    let cancelled = false;
    getProspectDetails(pid).then((d) => {
      if (cancelled || !d) return;
      const candidates = mapProspectToForm(d);
      setForm((s) => {
        const next = { ...s };
        for (const [k, v] of Object.entries(candidates)) {
          if (!next[k]) next[k] = v;
        }
        return next;
      });
      setPrefillBannerVisible(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the list of assessors once on mount.
  useEffect(() => {
    let cancelled = false;
    getAssessmentMembers().then((list) => {
      if (!cancelled) setAssessors(list);
    });
    return () => { cancelled = true; };
  }, []);

  // Reload slots when assessor or date changes (stale-guarded).
  useEffect(() => {
    if (!form.assessor_id || !form.schedule_date) {
      setSlots([]);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    getAssessorSlots(form.assessor_id, form.schedule_date).then((list) => {
      if (cancelled) return;
      // Sort by start time so the dropdown reads chronologically.
      const sorted = [...(list || [])].sort(
        (a, b) => (parseClockTime(a.scheduleStartTime) ?? 0) - (parseClockTime(b.scheduleStartTime) ?? 0)
      );
      setSlots(sorted);
      setSlotsLoading(false);
      // Auto-select the first available slot so the field isn't left empty.
      const first = sorted[0];
      setForm((s) =>
        first
          ? { ...s, slot_start: first.scheduleStartTime, slot_end: first.scheduleEndTime }
          : { ...s, slot_start: "", slot_end: "" }
      );
    });
    return () => { cancelled = true; };
  }, [form.assessor_id, form.schedule_date]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Reset every field back to its pristine state and clear attachments/slots.
  const reset = () => {
    setForm(makeInitialForm(todayISO));
    setAttachments([]);
    setSlots([]);
    setPrefillBannerVisible(false);
  };

  // Slot picker is disabled until an assessor + date are chosen and slots exist.
  const slotsDisabled = !form.assessor_id || !form.schedule_date || slotsLoading || slots.length === 0;

  // Smallest selectable assessment date is today.
  const minScheduleDate = todayISO;

  // Changing assessor or date clears the chosen slot (fresh slots will load).
  const setAssessor = (v) => setForm((s) => ({ ...s, assessor_id: v, slot_start: "", slot_end: "" }));
  const setScheduleDate = (v) => setForm((s) => ({ ...s, schedule_date: v, slot_start: "", slot_end: "" }));

  // Pick a slot from the dropdown ("09:00 AM|10:00 AM" → start/end).
  const setSlot = (value) => {
    const [start = "", end = ""] = value.split("|");
    setForm((s) => ({ ...s, slot_start: start, slot_end: end }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Please add your name and email so we can confirm.");
    }
    // Require scheduling only when the CRM returned assessors. If that call is
    // unreachable (empty list), don't block the booking from being submitted.
    if (assessors.length && (!form.assessor_id || !form.schedule_date || !form.slot_start || !form.slot_end)) {
      return toast.error("Please choose an assessor, preferred date and time slot.");
    }
    if (!form.consent) {
      return toast.error("Please tick the consent box so we can contact you.");
    }
    setSubmitting(true);
    try {
      await submitLead({
        type: "assessment",
        mode: prefillSource ? "staff" : "public",
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        // Backend lead unchanged: keep sending the locality (as the old
        // "Region / city" field did). The distinct suburb/city go to the CRM only.
        suburb: form.city || form.suburb,
        region: form.city,
        postal_code: form.postal_code,
        country: form.country,
        latitude: form.lat,
        longitude: form.lng,
        household_size: form.household_size
          ? Number(form.household_size)
          : undefined,
        current_system: form.current_system,
        interested_finance: form.interested_finance,
        consent: form.consent,
        crm_prospect_id: form.crm_prospect_id || undefined,
        notes: [
          `Energy source: ${form.energy_source || "—"}`,
          `Usage: ${form.usage_level || "—"}`,
          `System age: ${form.system_age || "—"}`,
          form.notes ? `\nNotes:\n${form.notes}` : "",
        ]
          .filter(Boolean)
          .join("\n"),
        internal_lead_data: {
          source: prefillSource || "Website Assessment",
          ...(initialPrefill?.home_show
            ? { home_show: initialPrefill.home_show }
            : {}),
          ...(initialPrefill?.specialist
            ? { specialist: initialPrefill.specialist }
            : {}),
          ...(initialPrefill?.installer
            ? { installer: initialPrefill.installer }
            : {}),
        },
      });

      // CRM convert (book the assessment) — fire-and-forget, isolated so it
      // can't affect submitLead. Use the prospect id when we have one; else
      // send contact details so the CRM can create + convert a fresh prospect.
      try {
        const convertFields = {
          assignAssessmentsId: form.assessor_id,
          message: form.notes,
          numberOfPeopleInHousehold: form.household_size,
          currentHotWaterSystem: FORM_TO_CRM_SYSTEM[form.current_system],
          currentEnergySource: FORM_TO_CRM_ENERGY[form.energy_source],
          approximateHotWaterUsage: FORM_TO_CRM_USAGE[form.usage_level],
          currentSystemAge: FORM_TO_CRM_AGE[form.system_age],
          scheduleDate: form.schedule_date,
          scheduleStartTime: form.slot_start,
          scheduleEndTime: form.slot_end,
        };
        if (form.crm_prospect_id) {
          convertFields.id = form.crm_prospect_id;
        } else {
          convertFields.name = form.name;
          convertFields.email = form.email;
          convertFields.phone = form.phone;
          // Street line only — suburb/city/postcode go as separate CRM fields.
          convertFields.address = form.street || form.address;
          convertFields.suburb = form.suburb;
          convertFields.city = form.city;
          convertFields.postcode = form.postal_code;
        }
        convertProspect(convertFields, attachments);
      } catch { /* never block the existing flow */ }

      clearAssessmentPrefill();
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Submission failed. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Card
        data-testid="assessment-success"
        className="rounded-3xl p-10 border-[#E2E8F0] bg-white text-center"
      >
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981] mb-5">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-[#0B132B]">
          Assessment request received
        </h2>
        <p className="mt-3 text-[#475569] leading-relaxed max-w-lg mx-auto">
          Thanks — an AeroMax specialist will reach out shortly to confirm a time. In the meantime, feel free to call <strong>0508 AEROMAX</strong> or email <a href="mailto:sales@aeromaxenergy.co.nz" className="text-[#10B981] font-semibold">sales@aeromaxenergy.co.nz</a>.
        </p>
      </Card>
    );
  }

  return (
    <Card
      data-testid="assessment-form"
      className="rounded-3xl p-6 sm:p-8 border-[#E2E8F0] bg-white"
    >
      {prefillBannerVisible && (
        <div
          data-testid="assessment-prefill-banner"
          className="mb-6 flex items-start gap-3 rounded-xl border border-[#10B981]/30 bg-[#F0FDF4] px-4 py-3 text-sm text-[#0B132B]"
        >
          <Info className="h-4 w-4 mt-0.5 text-[#10B981] shrink-0" />
          <div>
            <strong className="text-[#059669]">Details pre-filled.</strong>{" "}
            We've carried over your details. Review and edit any field before submitting.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-7">
        {/* Customer details */}
        <fieldset className="space-y-4">
          <legend className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981] mb-2">
            Customer details
          </legend>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Full name *">
              <Input
                data-testid="a-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
              />
            </FormField>
            <FormField label="Email *">
              <Input
                data-testid="a-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
              />
            </FormField>
            <FormField label="Phone">
              <Input
                data-testid="a-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
              />
            </FormField>
            <div className="sm:col-span-2">
              <FormField label="Address">
                <AddressAutocomplete
                  data-testid="a-address"
                  value={form.address}
                  onChange={(v) => set("address", v)}
                  onPlaceSelected={(p) => {
                    setForm((s) => ({
                      ...s,
                      address: p.formatted_address || s.address,
                      street: p.street_address || s.street,
                      suburb: p.sublocality || p.locality || s.suburb,
                      city: p.locality || s.city,
                      postal_code: p.postal_code || s.postal_code,
                      country: p.country || s.country,
                      lat: p.lat ?? s.lat,
                      lng: p.lng ?? s.lng,
                    }));
                  }}
                  placeholder="Start typing your NZ address…"
                  autoComplete="off"
                />
              </FormField>
            </div>
            <FormField label="Suburb">
              <Input
                data-testid="a-suburb"
                value={form.suburb}
                onChange={(e) => set("suburb", e.target.value)}
                placeholder="Suburb"
              />
            </FormField>
            <FormField label="City">
              <Input
                data-testid="a-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="e.g. Auckland"
              />
            </FormField>
            <FormField label="Postcode">
              <Input
                data-testid="a-postcode"
                value={form.postal_code}
                onChange={(e) => set("postal_code", e.target.value)}
                placeholder="Postcode"
              />
            </FormField>
          </div>
        </fieldset>

        {/* Home & usage */}
        <fieldset className="space-y-4 pt-3 border-t border-[#E2E8F0]">
          <legend className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981] mb-2">
            Home & usage
          </legend>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Number of people in household">
              <Input
                data-testid="a-household"
                type="number"
                min="1"
                value={form.household_size}
                onChange={(e) => set("household_size", e.target.value)}
              />
            </FormField>
            <FormField label="Current hot water system">
              <SelectField
                testId="a-system-type"
                value={form.current_system}
                onChange={(v) => set("current_system", v)}
                options={SYSTEM_TYPES}
              />
            </FormField>
            <FormField label="Current energy source">
              <SelectField
                testId="a-energy-source"
                value={form.energy_source}
                onChange={(v) => set("energy_source", v)}
                options={ENERGY_SOURCES}
              />
            </FormField>
            <FormField label="Approximate hot water usage">
              <SelectField
                testId="a-usage"
                value={form.usage_level}
                onChange={(v) => set("usage_level", v)}
                options={USAGE_LEVELS}
              />
            </FormField>
            <FormField label="Current system age">
              <SelectField
                testId="a-system-age"
                value={form.system_age}
                onChange={(v) => set("system_age", v)}
                options={SYSTEM_AGES}
              />
            </FormField>
          </div>
        </fieldset>

        {/* Assessment details */}
        <fieldset className="space-y-4 pt-3 border-t border-[#E2E8F0]">
          <legend className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#10B981] mb-2">
            Assessment details
          </legend>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Assessor *">
              <select
                data-testid="a-assessor"
                value={form.assessor_id}
                onChange={(e) => setAssessor(e.target.value)}
                className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
              >
                <option value="">Select an assessor…</option>
                {assessors.map((m) => (
                  <option key={m.id} value={m.id}>
                    {`${m.name || ""} ${m.lastName || ""}`.trim()}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Preferred date *">
              <Input
                data-testid="a-schedule-date"
                type="date"
                min={minScheduleDate}
                value={form.schedule_date}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
            </FormField>
          </div>
          <FormField
            label="Preferred time slot *"
            hint={
              !form.assessor_id || !form.schedule_date
                ? "Choose an assessor and date to see available time slots."
                : slotsLoading
                ? "Checking available time slots…"
                : slots.length === 0
                ? "No time slots are available on this date — please try another date."
                : "Select an available time slot."
            }
          >
            <select
              data-testid="a-slot"
              value={form.slot_start && form.slot_end ? `${form.slot_start}|${form.slot_end}` : ""}
              onChange={(e) => setSlot(e.target.value)}
              disabled={slotsDisabled}
              className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] disabled:bg-[#F8FAFC] disabled:text-[#94A3B8] disabled:cursor-not-allowed"
            >
              <option value="">{slots.length ? "Select a time slot…" : "No slots available"}</option>
              {slots.map((sl) => (
                <option key={slotKey(sl)} value={slotKey(sl)}>
                  {sl.scheduleStartTime} – {sl.scheduleEndTime}
                </option>
              ))}
            </select>
          </FormField>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Interested in green finance?">
              <SelectField
                testId="a-finance"
                value={form.interested_finance}
                onChange={(v) => set("interested_finance", v)}
                options={FINANCE_INTEREST}
              />
            </FormField>
            <FormField
              label="Photos"
              hint={
                attachments.length
                  ? `${attachments.length} file${attachments.length > 1 ? "s" : ""} attached: ${attachments.map((f) => f.name).join(", ")}`
                  : "Attach photos of your current system or switchboard. More can be emailed to sales@aeromaxenergy.co.nz."
              }
            >
              <div className="flex items-center gap-2.5 rounded-md border border-dashed border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2">
                <Camera className="h-4 w-4 text-[#10B981] shrink-0" />
                <input
                  data-testid="a-attachment"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setAttachments(Array.from(e.target.files || []))}
                  className="block w-full text-[12px] text-[#475569] file:mr-3 file:rounded-md file:border-0 file:bg-[#10B981]/10 file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-[#059669] hover:file:bg-[#10B981]/20"
                />
              </div>
            </FormField>
          </div>
          <FormField label="Notes / questions">
            <textarea
              data-testid="a-notes"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={4}
              placeholder="Anything you'd like our specialist to know — e.g. access, hot-water issues, finance questions…"
              className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 bg-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] resize-none"
            />
          </FormField>
        </fieldset>

        {/* Consent */}
        <fieldset className="pt-3 border-t border-[#E2E8F0]">
          <label className="flex items-start gap-3 text-sm text-[#475569] leading-relaxed cursor-pointer">
            <Checkbox
              data-testid="a-consent"
              checked={form.consent}
              onCheckedChange={(v) => set("consent", !!v)}
              className="mt-0.5"
            />
            <span>
              I agree for AeroMax Energy to contact me about my hot water heat pump assessment.
            </span>
          </label>
        </fieldset>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            data-testid="a-submit"
            type="submit"
            disabled={submitting}
            className="flex-1 rounded-full bg-[#10B981] hover:bg-[#059669] text-white py-6 font-semibold text-base"
          >
            {submitting ? "Sending…" : "Request Free Assessment"}
          </Button>
          <Button
            data-testid="a-clear"
            type="button"
            variant="outline"
            onClick={reset}
            disabled={submitting}
            className="rounded-full border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0B132B] py-6 font-semibold text-base sm:w-auto sm:px-8"
          >
            Clear
          </Button>
        </div>
        <p className="text-[11px] text-[#475569] leading-relaxed text-center">
          We'll be in touch shortly. Appointment scheduling will be available soon.
        </p>
      </form>
    </Card>
  );
}

export default function FreeHomeAssessment() {
  const location = useLocation();

  // Decide prefill + source on mount. Order of precedence:
  // 1. sessionStorage prefill (set by any Home Show staff flow)
  // 2. ?source=home_show_assessment / ?mode=staff query strings
  // 3. Default = Website Assessment
  const { initialPrefill, prefillSource } = useMemo(() => {
    const stored = getAssessmentPrefill();
    const params = new URLSearchParams(location.search);
    const qSource = params.get("source");
    const qMode = params.get("mode");
    const qProspectId = params.get("prospectId");

    // A prospectId in the URL (website CTA or emailed report link) is merged
    // into whatever prefill we have so the assessment links to the CRM prospect.
    const withPid = (pf) =>
      qProspectId ? { ...(pf || {}), crm_prospect_id: qProspectId } : pf;

    if (stored) {
      return {
        initialPrefill: withPid(stored),
        prefillSource: stored.source || "Home Show Assessment",
      };
    }
    if (qProspectId) {
      return { initialPrefill: withPid(null), prefillSource: null };
    }
    if (qSource === "home_show_assessment" || qMode === "staff") {
      return { initialPrefill: null, prefillSource: "Home Show Assessment" };
    }
    return { initialPrefill: null, prefillSource: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="free-home-assessment-page" className="bg-white">
      <Hero />
      <BenefitGrid />
      <section className="py-14 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <Reveal>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
              <Sparkles className="h-3 w-3" /> Free home assessment
            </span>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">
              Tell us about your home.
            </h2>
            <p className="mt-2 text-sm sm:text-base text-[#475569] leading-relaxed">
              The more we know up front, the more useful the assessment.
            </p>
          </Reveal>
          <div className="mt-7">
            <AssessmentForm
              initialPrefill={initialPrefill}
              prefillSource={prefillSource}
            />
          </div>
          <p className="mt-6 text-center text-xs text-[#475569]">
            Prefer to estimate first?{" "}
            <Link
              to="/savings-calculator"
              className="text-[#10B981] font-semibold hover:underline"
            >
              Run the savings calculator <ArrowRight className="inline h-3 w-3" />
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
